// Redux Utils
import { useSelector, useDispatch } from "react-redux";

// React Utils
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Packages
import QRCode from "react-qr-code";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Utils
import { formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { getColorByEstadoInvitacion } from "@/utils/invitacionUtils";

// Thunks
import { getInvitacionPorUid } from "@/features/invitacion/invitacionThunks";

// Services
import api from "@/services/api";



const ESTADOS_CON_QR = ["aceptada_gratis", "aceptada_descuento", "pago_pendiente", "aceptada_gratis_total", "pagada"];
const ESTADOS_CON_BOTON_PAGAR = ["aceptada_pago_total", "pago_pendiente"];

export default function InvitacionDetalle() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { invitacionDetalle, isLoadingInvitacion, failureInvitacion, errorStatusInvitacion } = useSelector(
    (state) => state.invitacion
  );

  const { uidInvitacion } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        await dispatch(getInvitacionPorUid({ uid: uidInvitacion }));
      } catch (err) {
        console.error("Error al cargar invitacion:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (uidInvitacion) {
      load();
    }
  }, [uidInvitacion, dispatch]);

  const handleIniciarPago = async () => {
    if (isInitiatingPayment) return;
    setIsInitiatingPayment(true);
    setPaymentError(null);

    try {
      const { data } = await api.post(
        `/invitaciones/${uidInvitacion}/iniciar-pago`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );

      if (data?.klapOrderId) {
        payKlapOrder(data.klapOrderId);
      } else {
        setPaymentError("No se pudo obtener el ID de la orden de pago");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || err?.message || "Error al iniciar el pago";
      setPaymentError(errorMessage);
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  const inv = invitacionDetalle;
  const colores = inv ? getColorByEstadoInvitacion(inv.estado) : null;

  const formatFechaInv = (fechaStr, zonaHoraria) => {
    if (!fechaStr) return "";
    const parsed = parseUTCDate(fechaStr, zonaHoraria || DEFAULT_TIMEZONE);
    if (!parsed) return "";
    return formatFechaHoraEvento(parsed.toJSDate(), zonaHoraria || DEFAULT_TIMEZONE);
  };

  const formatPrecio = (precio) => {
    if (!precio || precio === 0) return "GRATIS";
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(precio);
  };

  const isLoadingPage = isLoading || isLoadingInvitacion;

  // Vista de acceso denegado (403)
  if (!isLoadingPage && failureInvitacion && errorStatusInvitacion === 403) {
    return (
      <div className="min-h-screen bg-aw-bgbluedark flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto flex flex-col items-center text-center">
          <div className="w-24 h-24 rounded-full bg-red-500 flex items-center justify-center mb-6">
            <FontAwesomeIcon icon="fa-solid fa-ban" className="text-white text-5xl" />
          </div>
          <h1 className="text-3xl text-white font-bold mb-4">Acceso Denegado</h1>
          <p className="text-lg text-white mb-2">Esta invitacion no te pertenece</p>
          <p className="text-white/80 mb-8">
            Por favor, consulta unicamente la informacion de tus propias invitaciones.
          </p>
          <Link
            to={`/perfil/${user?.uid}`}
            className="bg-aw-green hover:bg-aw-green/90 text-aw-bgbluedark font-bold py-3 px-8 rounded-full transition-colors duration-200 inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left" />
            Volver a mi Perfil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 sm:p-6 lg:p-8 bg-aw-bgbluedark min-h-screen flex flex-col items-center">
        {/* Boton atras */}
        <div className="absolute top-8 left-8">
          <Link
            to={`/perfil/${user?.uid}`}
            className="text-white text-xl font-bold"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
          </Link>
        </div>

        <div className="w-full max-w-md mx-auto flex flex-col items-center mt-10">
          {/* Card principal */}
          <div className="relative w-full bg-aw-green rounded-2xl pt-12 pb-4 px-4 flex flex-col items-center shadow-xl">
            {/* Icono de estado */}
            <div
              className={`absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full flex items-center justify-center border-6 ${
                isLoadingPage ? "bg-gray-400" : colores?.bg || "bg-gray-400"
              } border-white z-10`}
            >
              {isLoadingPage ? (
                <Skeleton circle width={80} height={80} />
              ) : (
                <FontAwesomeIcon
                  icon="fa-solid fa-envelope-open-text"
                  className="text-white text-4xl"
                />
              )}
            </div>

            <div className="flex flex-col items-center w-full">
              {/* Estado y nombre del evento */}
              {isLoadingPage ? (
                <>
                  <Skeleton width={200} height={30} className="mb-2" />
                  <Skeleton width={250} height={20} className="mb-2" />
                  <Skeleton width={180} height={20} className="mb-4" />
                </>
              ) : (
                <>
                  <span
                    className={`text-xs font-bold px-3 py-1 rounded-full mb-2 ${colores?.badge}`}
                  >
                    {colores?.label}
                  </span>
                  <div className="text-3xl text-aw-bgbluedark text-center">
                    <span className="text-aw-dark font-bold">
                      {inv?.eventoNombre}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-aw-blue text-center mb-2">
                    Tipo:{" "}
                    <span className="font-bold text-aw-gray">
                      {inv?.tipoTicketNombre}
                    </span>
                  </div>
                </>
              )}

              {/* Detalles */}
              <div className="flex w-full text-sm my-2">
                <table className="w-full">
                  <tbody>
                    <tr className="h-6">
                      <td className="w-5/12 font-medium">Fecha</td>
                      <td className="w-7/12 font-semibold text-right">
                        {isLoadingPage ? (
                          <Skeleton width={140} />
                        ) : (
                          formatFechaInv(
                            inv?.fechaHoraInicio,
                            inv?.eventoZonaHoraria
                          )
                        )}
                      </td>
                    </tr>
                    {inv?.modalidadCedida && (
                      <tr className="h-6">
                        <td className="w-5/12 font-medium">Modalidad</td>
                        <td className="w-7/12 font-semibold text-right capitalize">
                          {inv.modalidadCedida.replace(/_/g, " ")}
                        </td>
                      </tr>
                    )}
                    {inv?.precioCedido !== undefined && inv?.precioCedido !== null && (
                      <tr className="h-6">
                        <td className="w-5/12 font-medium">Precio</td>
                        <td className="w-7/12 font-semibold text-right">
                          {formatPrecio(inv.precioCedido)}
                        </td>
                      </tr>
                    )}
                    {inv?.eventoUbicacion && (
                      <tr className="h-6">
                        <td className="w-5/12 font-medium">Ubicacion</td>
                        <td className="w-7/12 font-semibold text-right text-xs">
                          {inv.eventoUbicacion}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Botón Google Maps */}
              {!isLoadingPage && inv?.direccion && (
                <div className="flex w-full max-w-xs mb-2 mt-1">
                  <button
                    className="flex-1 bg-aw-green font-bold text-aw-bluedark rounded-lg py-2 text-[0.65rem] border-2 border-aw-bgbluedark hover:bg-aw-bluedark hover:text-aw-green transition hover:scale-95 duration-300"
                    onClick={() => {
                      const query = encodeURIComponent(inv.direccion);
                      window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
                    }}
                  >
                    Ir a Google Maps
                  </button>
                </div>
              )}

              {/* Boton pagar (si aplica) */}
              {!isLoadingPage &&
                inv &&
                ESTADOS_CON_BOTON_PAGAR.includes(inv.estado) &&
                inv.precioCedido > 0 && (
                  <div className="flex flex-col items-center w-full gap-2 mb-4">
                    {paymentError && (
                      <p className="text-red-600 text-xs text-center">
                        {paymentError}
                      </p>
                    )}
                    <button
                      onClick={handleIniciarPago}
                      disabled={isInitiatingPayment}
                      className="flex-1 w-full bg-aw-bluedark border-aw-bluedark text-aw-green font-bold rounded-lg py-2 text-sm border-2 hover:bg-aw-green hover:text-aw-bluedark transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isInitiatingPayment ? (
                        <>
                          <FontAwesomeIcon
                            icon="fa-solid fa-spinner"
                            className="animate-spin"
                          />
                          Preparando pago...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon="fa-solid fa-credit-card" />
                          Pagar {formatPrecio(inv.precioCedido)}
                        </>
                      )}
                    </button>
                  </div>
                )}

              {/* Mensaje entrada exitosa */}
              {!isLoadingPage && inv?.estado === "entrada_exitosa" && (
                <div className="flex flex-col items-center gap-2 mb-4">
                  <FontAwesomeIcon
                    icon="fa-solid fa-circle-check"
                    className="text-teal-700 text-4xl"
                  />
                  <p className="text-aw-bgbluedark font-bold text-center">
                    ¡Entrada registrada!
                  </p>
                </div>
              )}

              {/* Condiciones de la invitación — siempre visible */}
              {!isLoadingPage && inv && (
                <div className="w-full bg-aw-bgbluedark/10 rounded-xl p-3 mt-2 flex flex-col gap-2">
                  <p className="text-aw-bgbluedark text-xs font-bold flex items-center gap-1">
                    <FontAwesomeIcon icon="fa-solid fa-circle-info" />
                    Condiciones de tu invitación
                  </p>

                  {/* Estado: gratis */}
                  {inv.estado === "aceptada_gratis" && (
                    <>
                      <p className="text-aw-bgbluedark/80 text-xs">
                        Tu invitación fue aceptada como <strong>entrada gratuita</strong>. Para ingresar sin costo, debes presentar este QR <strong>antes</strong> de los horarios indicados a continuación.
                      </p>
                      {inv.invitacionHoraLimiteGratuita && (
                        <p className="text-aw-bgbluedark/80 text-xs">
                          🎟️ <strong>Entrada gratis</strong> si llegas antes del{" "}
                          <strong>{formatFechaInv(inv.invitacionHoraLimiteGratuita, inv.eventoZonaHoraria)}</strong>.
                          {inv.invitacionDescuento
                            ? ` Si llegas después de esa hora, tu ticket se actualizará al momento del escaneo y deberás pagar ${formatPrecio(inv.invitacionDescuento)}.`
                            : " Si llegas después, ya no aplicará la entrada gratuita."}
                        </p>
                      )}
                      {inv.invitacionHoraLimiteDescuento && inv.invitacionDescuento && (
                        <p className="text-aw-bgbluedark/80 text-xs">
                          💳 <strong>Descuento de {formatPrecio(inv.invitacionDescuento)}</strong> si llegas antes del{" "}
                          <strong>{formatFechaInv(inv.invitacionHoraLimiteDescuento, inv.eventoZonaHoraria)}</strong>.
                          {" "}Si llegas después, deberás pagar el precio completo del ticket.
                        </p>
                      )}
                      {!inv.invitacionHoraLimiteGratuita && !inv.invitacionHoraLimiteDescuento && (
                        <p className="text-aw-bgbluedark/80 text-xs">
                          No hay restricción de horario configurada. Tu entrada gratuita es válida durante todo el evento.
                        </p>
                      )}
                    </>
                  )}

                  {/* Estado: con descuento */}
                  {inv.estado === "aceptada_descuento" && (
                    <>
                      <p className="text-aw-bgbluedark/80 text-xs">
                        Tu invitación fue aceptada <strong>con descuento{inv.invitacionDescuento != null ? ` de ${formatPrecio(inv.invitacionDescuento)}` : ""}</strong>. El pago se habilitará en la puerta al momento de escanear tu QR.
                      </p>
                      {inv.invitacionHoraLimiteDescuento && (
                        <p className="text-aw-bgbluedark/80 text-xs">
                          ⚠️ Debes llegar <strong>antes del{" "}
                          {formatFechaInv(inv.invitacionHoraLimiteDescuento, inv.eventoZonaHoraria)}</strong> para aplicar el descuento.
                          {" "}Si llegas después de esa hora, tu ticket se actualizará y deberás pagar el precio completo.
                        </p>
                      )}
                      {!inv.invitacionHoraLimiteDescuento && (
                        <p className="text-aw-bgbluedark/80 text-xs">
                          No hay restricción de horario configurada. El descuento aplica durante todo el evento.
                        </p>
                      )}
                    </>
                  )}

                  {/* Aviso identificación — siempre visible */}
                  <div className="flex items-start gap-2 pt-1 border-t border-aw-bgbluedark/20">
                    <FontAwesomeIcon icon="fa-solid fa-id-card" className="text-aw-bgbluedark mt-0.5 shrink-0" />
                    <p className="text-aw-bgbluedark text-xs">
                      Recuerda que debes presentar tu <strong>documento de identificación</strong> para comprobar tu identidad para la invitación.
                    </p>
                  </div>
                </div>
              )}

              {/* Separador decorativo */}
              <div className="w-full relative">
                <div className="absolute -top-2.5 -left-4 w-5 h-10 bg-aw-bgbluedark rounded-r-full"></div>
                <hr
                  className="w-11/12 border-aw-bgbluedark my-2 mx-auto border-dashed"
                  style={{ borderTopWidth: 2 }}
                />
                <div className="absolute -top-2.5 -right-4 w-5 h-10 bg-aw-bgbluedark rounded-l-full"></div>
              </div>

              {/* QR Code */}
              <div className="flex flex-col justify-center items-center w-full max-w-xs gap-2 mt-2 mb-2">
                {isLoadingPage ? (
                  <div className="w-11/12">
                    <Skeleton width="100%" height={200} />
                  </div>
                ) : inv?.qr && ESTADOS_CON_QR.includes(inv.estado) ? (
                  <div className="w-11/12 flex items-center justify-center bg-white p-4 rounded-lg">
                    <QRCode
                      value={inv.qr}
                      size={200}
                      style={{
                        height: "auto",
                        maxWidth: "100%",
                        width: "100%",
                      }}
                    />
                  </div>
                ) : (
                  !isLoadingPage && (
                    <div className="w-11/12 flex flex-col items-center justify-center bg-aw-bgbluedark/30 rounded-lg py-6 px-4 text-center">
                      <FontAwesomeIcon
                        icon={inv?.estado === "entrada_exitosa" ? "fa-solid fa-circle-check" : "fa-solid fa-qrcode"}
                        className={`text-4xl mb-2 ${inv?.estado === "entrada_exitosa" ? "text-teal-700" : "text-aw-bgbluedark opacity-30"}`}
                      />
                      <p className="text-aw-bgbluedark text-xs">
                        {inv?.estado === "entrada_exitosa"
                          ? "Esta invitación ya fue utilizada."
                          : "El código QR estará disponible cuando tu solicitud sea aceptada."}
                      </p>
                    </div>
                  )
                )}

                {/* Imagen del tipo de ticket o del evento */}
                {!isLoadingPage && (() => {
                  const imagenSrc =
                    inv?.useImgEvento === 0 && inv?.imgTicket
                      ? inv.imgTicket
                      : inv?.imgEvento;
                  return imagenSrc ? (
                    <img
                      src={imagenSrc}
                      alt={inv?.eventoNombre || "Evento"}
                      className="w-11/12 rounded-lg object-cover max-h-48"
                    />
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          <div className="w-full text-[10px] text-white text-center mt-2">
            Las invitaciones son gestionadas directamente por la Empresa
            organizadora del evento. AppWhoop actua unicamente como plataforma
            de gestion y no interviene en las decisiones de admision.
          </div>
        </div>
      </div>

    </>
  );
}
